import React from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { SideNav, Icon, Logo } from "../../designSystem";
import { useCompanyContext } from "../companies/useCompanyContext";
import { useBreakpoint, usePrefersReducedMotion, BreakpointProvider } from "./breakpoints";
import { useNavDestinations, NavDestination } from "../sidebar/navModel";

/**
 * PR C — Responsive application shell (FR-009..013, US1-AC2, US1-AC3).
 *
 * One shell, one destination model ({@link useNavDestinations}), two presentations
 * driven by a single breakpoint source ({@link useBreakpoint}):
 *   - desktop (>= 1024px): DS `SideNav` icon rail (72px) that expands to 248px on
 *     hover to reveal labels, with the active-state pill.
 *   - tablet (768–1024px): the same rail, collapsed to icon-only.
 *   - mobile (< 768px): a top header bar with a hamburger that opens a full-height
 *     slide-in drawer listing the same destinations, with page-level tabs surfaced
 *     as nested secondary items under their active parent.
 *
 * All transitions are ~120–280ms ease-out and are suppressed under
 * `prefers-reduced-motion`. Every nav control is a focusable button with the
 * browser/DS focus ring (FR-013, FR-022).
 */
interface Props {
  children: React.ReactNode;
  /** Top app bar (search, profile, etc.) rendered above the scrollable content column. */
  appHeader?: React.ReactNode;
}

const EASE = "200ms ease-out";

export const AppShell: React.FC<Props> = ({ children, appHeader }) => {
  return (
    <BreakpointProvider>
      <AppShellFrame appHeader={appHeader}>{children}</AppShellFrame>
    </BreakpointProvider>
  );
};

const AppShellFrame: React.FC<Props> = ({ children, appHeader }) => {
  const history = useHistory();
  const breakpoint = useBreakpoint();
  const reducedMotion = usePrefersReducedMotion();
  const { destinations, footerLinks } = useNavDestinations();
  const { displayLogo, displayName } = useCompanyContext();

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const isMobile = breakpoint === "mobile";
  // Desktop shows the full-width rail with labels by default; tablet keeps the
  // icon-only rail to preserve horizontal space.
  const expanded = breakpoint === "desktop";

  const activeId = destinations.find((d) => d.active)?.id;
  const transition = reducedMotion ? "none" : EASE;

  const navigate = React.useCallback(
    (to: string) => {
      history.push(to);
      setDrawerOpen(false);
    },
    [history]
  );

  // Close the drawer whenever we leave the mobile breakpoint so the rail is never
  // hidden behind a stale overlay after a resize/rotate.
  React.useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const footer = (
    <div className="u-gap-xs" style={{ display: "flex", flexDirection: "column" }}>
      {footerLinks.map((link) => (
        <a
          key={link.id}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          title={link.label}
          className="ds-nav-link"
          style={{
            padding: expanded ? undefined : 0,
            justifyContent: expanded ? "flex-start" : "center",
          }}
        >
          <Icon name={link.icon} size={22} />
          {expanded ? <span className="u-whitespace-nowrap">{link.label}</span> : null}
        </a>
      ))}
    </div>
  );

  const header =
    appHeader && React.isValidElement(appHeader)
      ? React.cloneElement(appHeader as React.ReactElement<{ onOpenMenu?: () => void }>, {
          onOpenMenu: isMobile ? () => setDrawerOpen(true) : undefined,
        })
      : appHeader;

  return (
    <div data-testid="app-shell" className="ds-app-shell">
      {!isMobile && (
        <div data-testid="app-shell-rail" style={{ flex: "none", height: "100%" }}>
          <SideNav
            items={destinations.map((d) => ({ id: d.id, icon: d.icon, label: d.label, badge: d.badge }))}
            active={activeId}
            onSelect={(id: string) => {
              const dest = destinations.find((d) => d.id === id);
              if (dest) navigate(dest.to);
            }}
            expanded={expanded}
            brandSrc={displayLogo}
            brandAlt={displayName}
            footer={footer}
            style={{ transition }}
          />
        </div>
      )}

      <div className="ds-app-shell-main">
        {header ? <div data-testid="app-shell-header">{header}</div> : null}
        <div id="dashboard-container" className="ds-app-shell-scroll">
          {/* Design system: the main content pane caps at --content-max (1280px) and
              centres, across all views. The grey page surface bleeds full-width behind it. */}
          <div className="u-content-max">{children}</div>
        </div>
      </div>

      {isMobile && (
        <MobileDrawer
          open={drawerOpen}
          destinations={destinations}
          activeId={activeId}
          transition={transition}
          onClose={() => setDrawerOpen(false)}
          onNavigate={navigate}
          footerLinks={footerLinks}
        />
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Mobile slide-in drawer (FR-010, FR-012, US1-AC2, US1-AC3)           */
/* ------------------------------------------------------------------ */

interface DrawerProps {
  open: boolean;
  destinations: NavDestination[];
  activeId?: string;
  transition: string;
  onClose: () => void;
  onNavigate: (to: string) => void;
  footerLinks: {
    id: string;
    label: string;
    href: string;
    icon: import("../../designSystem/components/core/Icon").IconName;
  }[];
}

const MobileDrawer: React.FC<DrawerProps> = ({
  open,
  destinations,
  activeId,
  transition,
  onClose,
  onNavigate,
  footerLinks,
}) => {
  const { displayLogo, displayName } = useCompanyContext();
  if (typeof document === "undefined") return null;
  // Rendered into document.body so the fixed overlay can never be clipped by an
  // ancestor's stacking context, `overflow`, or transform-induced containing block.
  return createPortal(
    <>
      {/* Scrim */}
      <div
        onClick={onClose}
        aria-hidden
        className="ds-scrim"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: transition === "none" ? "none" : "opacity 200ms ease-out",
        }}
      />
      <nav
        data-testid="app-shell-drawer"
        aria-label="Main navigation"
        aria-hidden={!open}
        className="ds-drawer"
        style={{
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: transition === "none" ? "none" : "transform 200ms ease-out",
        }}
      >
        <div className="ds-drawer-header">
          <div className="ds-drawer-logo-slot">
            <Logo brandSrc={displayLogo} brandAlt={displayName} maxWidth={180} maxHeight={40} color="var(--slate)" />
          </div>
          <button type="button" aria-label="Close navigation menu" onClick={onClose} className="ds-drawer-close">
            <Icon name="x" size={22} />
          </button>
        </div>

        <div className="ds-drawer-body">
          {destinations.map((dest) => (
            <React.Fragment key={dest.id}>
              <DrawerItem
                label={dest.label}
                icon={dest.icon}
                active={dest.id === activeId}
                badge={dest.badge}
                onClick={() => onNavigate(dest.to)}
              />
              {/* Page-level tabs nested under their active parent (US1-AC3). */}
              {dest.id === activeId && dest.secondary && dest.secondary.length > 0 && (
                <div style={{ marginLeft: 34, display: "flex", flexDirection: "column" }}>
                  {dest.secondary.map((sec) => (
                    <DrawerSecondaryItem
                      key={sec.id}
                      label={sec.label}
                      active={sec.active}
                      onClick={() => onNavigate(sec.to)}
                    />
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="ds-drawer-footer">
          {footerLinks.map((link) => (
            <a key={link.id} href={link.href} target="_blank" rel="noopener noreferrer" className="ds-nav-link">
              <Icon name={link.icon} size={22} />
              <span>{link.label}</span>
            </a>
          ))}
        </div>
      </nav>
    </>,
    document.body
  );
};

const DrawerItem: React.FC<{
  label: string;
  icon: import("../../designSystem/components/core/Icon").IconName;
  active: boolean;
  badge?: string | number;
  onClick: () => void;
}> = ({ label, icon, active, badge, onClick }) => (
  <button
    type="button"
    aria-current={active ? "page" : undefined}
    onClick={onClick}
    className={`ds-nav-item${active ? " ds-nav-item--active" : ""}`}
  >
    <Icon name={icon} size={22} />
    <span style={{ flex: 1 }}>{label}</span>
    {badge != null ? (
      <span
        data-testid="nav-badge-dot"
        style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--green-success)", flex: "none" }}
      />
    ) : null}
  </button>
);

const DrawerSecondaryItem: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
  label,
  active,
  onClick,
}) => (
  <button
    type="button"
    aria-current={active ? "page" : undefined}
    onClick={onClick}
    className={`ds-nav-secondary${active ? " ds-nav-secondary--active" : ""}`}
  >
    {label}
  </button>
);
